import { 
  collection, 
  doc, 
  onSnapshot, 
  orderBy, 
  query, 
  updateDoc, 
  where, 
  addDoc, 
  deleteDoc,
  Timestamp,
  limit,
  startAfter,
  getDocs,
  setDoc
} from 'firebase/firestore'
import { deleteUser as deleteAuthUser, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { auth, db, functions, getAdminUserCreateAuth } from '../../firebase'
import { 
  Box, 
  Button, 
  Chip, 
  Collapse,
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Stack, 
  Switch, 
  TextField, 
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material'
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  History as HistoryIcon
} from '@mui/icons-material'
import { searchService } from '../../services/searchService'
import {
  type CategoryCatalogItem,
  listHasCategory,
  addCategoryIdToList,
  removeCategoryFromList,
  normalizeCategoryIdList,
  toCategoryKeys,
  LEGACY_CATEGORY_PREFIX,
} from '../../utils/categoryResolver'
import {
  createAdminMessageLog,
  finalizeAdminMessageLog,
  type RecipientDraft,
  type AdminMessageFilterSnapshot,
} from '../../services/adminMessageLogService'

type UserStatus = 'active' | 'inactive'
type DeactivatedBy = 'self' | 'admin'
type UserType = 'buyer' | 'seller' | 'admin'

/** مفتاح افتراضي يُستخدم في فلتر فئات التجار لالتقاط من لا يوجد لديه أي فئة */
const NO_CATEGORY_KEY = '__no_category__'

interface SellerCategoryOption {
  key: string
  label: string
  isLegacy: boolean
  count: number
}

interface BuyerStats {
  totalRequests?: number
  completedRequests?: number
  cancelledRequests?: number
  expiredRequests?: number
  buyerScore?: number
  acceptanceRate?: number
  cancellationRate?: number
  isRestricted?: boolean
  restrictionLevel?: number
  restrictionEndDate?: any
}

interface UserDoc {
  id: string
  name?: string
  phone?: string
  email?: string
  emailProxy?: string
  type?: UserType
  isActive?: boolean // للتوافق مع النظام القديم
  status?: UserStatus
  isDeleted?: boolean
  deletedAt?: any
  deletedBy?: 'self' | 'admin' | string
  deactivatedBy?: DeactivatedBy
  deactivatedAt?: any
  deactivatedReason?: string
  address?: string
  categories?: string[]
  allowedCategories?: string[]
  rating?: number
  isVerified?: boolean
  isSuperSeller?: boolean
  createdAt?: any
  updatedAt?: any
  buyerStats?: BuyerStats
}

interface UserStats {
  totalUsers: number
  activeBuyers: number
  inactiveBuyers: number
  activeSellers: number
  inactiveSellers: number
  admins: number
}

export default function UsersPage() {
  const [users, setUsers] = useState<UserDoc[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserDoc[]>([])
  const [edit, setEdit] = useState<UserDoc | null>(null)
  const [allCategories, setAllCategories] = useState<CategoryCatalogItem[]>([])
  const isProd = typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.MODE === 'production'
  const [createDialog, setCreateDialog] = useState(false)
  const [createForm, setCreateForm] = useState<{ name: string; phone: string; type: UserType; password: string; confirm: string }>(
    { name: '', phone: '', type: 'buyer', password: '', confirm: '' }
  )
  const [deleteDialog, setDeleteDialog] = useState<UserDoc | null>(null)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [typeFilter, setTypeFilter] = useState<UserType | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('all')
  // فلتر الفئات — يظهر فقط عند اختيار "تاجر" في نوع المستخدم
  const [sellerCategoryKeys, setSellerCategoryKeys] = useState<Set<string>>(new Set())
  const [sellerCategorySearch, setSellerCategorySearch] = useState('')
  const [sellerCategoryFilterOpen, setSellerCategoryFilterOpen] = useState(false)
  const [includeAllowedCategories, setIncludeAllowedCategories] = useState(false)
  const [stats, setStats] = useState<UserStats>({
    totalUsers: 0,
    activeBuyers: 0,
    inactiveBuyers: 0,
    activeSellers: 0,
    inactiveSellers: 0,
    admins: 0
  })
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' })
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false)
  const [sendMessageDialog, setSendMessageDialog] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [messageForm, setMessageForm] = useState({ title: '', body: '', type: 'notification' as 'notification' | 'email' })

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(q, snap => {
      const rows: UserDoc[] = snap.docs.map(d => ({ 
        id: d.id, 
        ...(d.data() as any),
        // تحويل isActive القديم إلى status الجديد للتوافق
        status: d.data().status || (d.data().isActive ? 'active' : 'inactive')
      }))
      setUsers(rows)
      calculateStats(rows)
    })
    return () => unsub()
  }, [])

  // فتح مستخدم مباشرة من رابط مثل /users?userId=... أو /users?q=...
  useEffect(() => {
    const userId = (searchParams.get('userId') || '').trim()
    const q = (searchParams.get('q') || '').trim()
    if (userId) setSearchQuery(userId)
    else if (q) setSearchQuery(q)
  }, [searchParams])

  // تحميل قائمة الفئات المتاحة من collection "categories" (نفس إعدادات الفئات)
  // استخدام onSnapshot للتحديث التلقائي عند إضافة/حذف/تعديل الفئات
  useEffect(() => {
    console.log('🔄 UsersPage: بدء الاستماع لتغييرات collection "categories"...')
    
    // استخدام collection مباشرة بدون orderBy لتجنب مشاكل الفهرس
    const unsubscribe = onSnapshot(collection(db, 'categories'), (snapshot) => {
      try {
        const cats: CategoryCatalogItem[] = []
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data() as any
          const name = String(data?.name ?? data?.nameAr ?? data?.name_ar ?? '').trim()
          if (!name) return
          cats.push({
            id: docSnap.id,
            name,
            nameEn: String(data?.nameEn ?? data?.name_en ?? '').trim(),
            order: typeof data.order === 'number' ? data.order : 999999,
          })
        })

        cats.sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order
          return a.name.localeCompare(b.name, 'ar')
        })

        if (cats.length > 0) {
          setAllCategories(cats)
          console.log('✅ UsersPage: تم تحديث الفئات من "categories" (تحديث تلقائي):', cats.length)
        } else {
          console.warn('⚠️ UsersPage: لا توجد فئات في collection "categories"')
        }
      } catch (e) {
        console.error('❌ UsersPage: خطأ في معالجة تغييرات "categories":', e)
        // لا نضع [] هنا - نترك القائمة الحالية حتى لا تختفي الفئات
      }
    }, (error) => {
      console.error('❌ UsersPage: خطأ في الاستماع لتغييرات "categories":', error)
      // لا نضع [] هنا - نترك القائمة الحالية حتى لا تختفي الفئات
    })

    return () => {
      console.log('🛑 UsersPage: إيقاف الاستماع لتغييرات "categories"')
      unsubscribe()
    }
  }, [])

  // حساب الإحصائيات
  const calculateStats = (usersList: UserDoc[]) => {
    const newStats = {
      totalUsers: usersList.length,
      activeBuyers: usersList.filter(u => u.type === 'buyer' && (u.status === 'active' || u.isActive)).length,
      inactiveBuyers: usersList.filter(u => u.type === 'buyer' && (u.status === 'inactive' || !u.isActive)).length,
      activeSellers: usersList.filter(u => u.type === 'seller' && (u.status === 'active' || u.isActive)).length,
      inactiveSellers: usersList.filter(u => u.type === 'seller' && (u.status === 'inactive' || !u.isActive)).length,
      admins: usersList.filter(u => u.type === 'admin').length
    }
    setStats(newStats)
  }

  // مفاتيح فئات كل تاجر: الفئات التي يعمل بها، ونضيف الفئات المسموحة بها عند تفعيل الخيار
  const sellerCategoryKeysMap = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const u of users) {
      if (u.type !== 'seller') continue
      const raw = includeAllowedCategories
        ? [...(u.categories ?? []), ...(u.allowedCategories ?? [])]
        : (u.categories ?? [])
      map.set(u.id, toCategoryKeys(raw, allCategories))
    }
    return map
  }, [users, allCategories, includeAllowedCategories])

  const sellerCategoryFilterActive = typeFilter === 'seller' && sellerCategoryKeys.size > 0

  // خيارات الفلتر: كل فئة في الإعدادات + أسماء قديمة لم تعد موجودة، مع عدد التجار في كل فئة
  const sellerCategoryOptions = useMemo<SellerCategoryOption[]>(() => {
    const counts = new Map<string, number>()
    const labelByKey = new Map<string, string>()
    for (const cat of allCategories) {
      labelByKey.set(cat.id, cat.name || cat.nameEn || cat.id)
    }
    for (const keys of sellerCategoryKeysMap.values()) {
      for (const key of keys) {
        counts.set(key, (counts.get(key) ?? 0) + 1)
        // اسم قديم لم يعد موجوداً في الإعدادات — نبقيه قابلاً للاختيار حتى لا يختفي تاجر من الفلتر
        if (key.startsWith(LEGACY_CATEGORY_PREFIX) && !labelByKey.has(key)) {
          labelByKey.set(key, key.slice(LEGACY_CATEGORY_PREFIX.length))
        }
      }
    }
    const options: SellerCategoryOption[] = []
    for (const [key, label] of labelByKey) {
      options.push({
        key,
        label: label || key,
        isLegacy: key.startsWith(LEGACY_CATEGORY_PREFIX),
        count: counts.get(key) ?? 0,
      })
    }
    const byLabel = (a: SellerCategoryOption, b: SellerCategoryOption) =>
      a.label.localeCompare(b.label, 'ar')
    return [
      ...options.filter((o) => !o.isLegacy).sort(byLabel),
      ...options.filter((o) => o.isLegacy).sort(byLabel),
    ]
  }, [allCategories, sellerCategoryKeysMap])

  const sellersWithoutCategory = useMemo(() => {
    let n = 0
    for (const [, keys] of sellerCategoryKeysMap) {
      if (keys.length === 0) n++
    }
    return n
  }, [sellerCategoryKeysMap])

  const visibleSellerCategoryOptions = useMemo(() => {
    const q = sellerCategorySearch.trim()
    if (!q) return sellerCategoryOptions
    return sellerCategoryOptions.filter((o) => o.label.includes(q))
  }, [sellerCategoryOptions, sellerCategorySearch])

  const toggleSellerCategory = (key: string) => {
    setSellerCategoryKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectAllSellerCategories = () => {
    setSellerCategoryKeys(new Set(sellerCategoryOptions.map((o) => o.key)))
  }

  const clearAllSellerCategories = () => setSellerCategoryKeys(new Set())

  const passesSellerCategoryFilter = (user: UserDoc) => {
    const keys = sellerCategoryKeysMap.get(user.id) ?? []
    if (keys.length === 0) return sellerCategoryKeys.has(NO_CATEGORY_KEY)
    return keys.some((k) => sellerCategoryKeys.has(k))
  }

  const totalSellersCount = useMemo(() => sellerCategoryKeysMap.size, [sellerCategoryKeysMap])

  // لقطة الفلاتر وقت الإرسال — تُحفظ مع سجل الرسالة لمعرفة لماذا وصلت لهؤلاء
  // الحقول الغائبة تُحذف بالكامل (وليس undefined) لأن Firestore يرفض undefined
  const currentFilterSnapshot = (matchedCount: number): AdminMessageFilterSnapshot => ({
    ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
    ...(sellerCategoryFilterActive
      ? {
          categoryKeys: [...sellerCategoryKeys],
          categoryNames: [...sellerCategoryKeys].map(
            (key) => sellerCategoryOptions.find((o) => o.key === key)?.label || key
          ),
        }
      : {}),
    ...(includeAllowedCategories ? { includeAllowedCategories: true } : {}),
    matchedCount,
  })

  const sellersMatchingCategoryFilter = useMemo(() => {
    if (!sellerCategoryFilterActive) return totalSellersCount
    let n = 0
    for (const keys of sellerCategoryKeysMap.values()) {
      if (keys.length === 0) {
        if (sellerCategoryKeys.has(NO_CATEGORY_KEY)) n++
      } else if (keys.some((k) => sellerCategoryKeys.has(k))) {
        n++
      }
    }
    return n
  }, [sellerCategoryKeysMap, sellerCategoryFilterActive, sellerCategoryKeys, totalSellersCount])

  // فلترة المستخدمين
  useEffect(() => {
    let filtered = users

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(u => 
        u.id?.toLowerCase() === q ||
        u.id?.toLowerCase().includes(q) ||
        u.name?.toLowerCase().includes(q) ||
        u.phone?.includes(searchQuery)
      )
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(u => u.type === typeFilter)
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(u => 
        statusFilter === 'active' 
          ? (u.status === 'active' || u.isActive)
          : (u.status === 'inactive' || !u.isActive)
      )
    }

    // فلتر الفئات يُطبَّق على التجار فقط، ويعمل بأي تكرار للفلاتر (لا يعوّض الفلاتر الأخرى)
    if (sellerCategoryFilterActive) {
      filtered = filtered.filter(u => u.type !== 'seller' || passesSellerCategoryFilter(u))
    }

    setFilteredUsers(filtered)
    setPage(1)
  }, [users, searchQuery, typeFilter, statusFilter, sellerCategoryFilterActive, sellerCategoryKeys, sellerCategoryKeysMap])

  // تبديل حالة التاجر السوبر
  const toggleSuperSeller = async (user: UserDoc) => {
    try {
      const currentStatus = user.isSuperSeller || false
      const newStatus = !currentStatus
      
      await updateDoc(doc(db, 'users', user.id), {
        isSuperSeller: newStatus,
        updatedAt: Timestamp.now()
      })
      
      // تسجيل النشاط
      await addDoc(collection(db, 'admin_activity_logs'), {
        adminId: 'current_admin',
        action: newStatus ? 'تفعيل تاجر سوبر' : 'تعطيل تاجر سوبر',
        targetUserId: user.id,
        targetUserName: user.name || 'غير محدد',
        details: `تم ${newStatus ? 'تفعيل' : 'تعطيل'} خاصية التاجر السوبر`,
        timestamp: Timestamp.now()
      })

      setSnackbar({
        open: true,
        message: `تم ${newStatus ? 'تفعيل' : 'تعطيل'} خاصية التاجر السوبر بنجاح`,
        severity: 'success'
      })
    } catch (error) {
      console.error('خطأ في تبديل حالة التاجر السوبر:', error)
      setSnackbar({
        open: true,
        message: 'حدث خطأ في تغيير حالة التاجر السوبر',
        severity: 'error'
      })
    }
  }

  // تبديل حالة المستخدم
  const toggleUserStatus = async (user: UserDoc, reason?: string) => {
    try {
      const currentStatus = user.status || (user.isActive ? 'active' : 'inactive')
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
      
      const updateData: any = {
        status: newStatus,
        isActive: newStatus === 'active', // للتوافق مع النظام القديم
        updatedAt: Timestamp.now()
      }

      if (newStatus === 'inactive') {
        updateData.deactivatedBy = 'admin'
        updateData.deactivatedAt = Timestamp.now()
        updateData.deactivatedReason = reason || 'تم التعطيل بواسطة الإدارة'
      } else {
        updateData.deactivatedBy = null
        updateData.deactivatedAt = null
        updateData.deactivatedReason = null
      }

      await updateDoc(doc(db, 'users', user.id), updateData)
      
      // تسجيل النشاط
      await addDoc(collection(db, 'admin_activity_logs'), {
        adminId: 'current_admin', // TODO: الحصول على معرف الأدمن الحالي
        action: newStatus === 'active' ? 'تفعيل مستخدم' : 'تعطيل مستخدم',
        targetUserId: user.id,
        targetUserName: user.name || 'غير محدد',
        details: reason || `تم ${newStatus === 'active' ? 'تفعيل' : 'تعطيل'} المستخدم`,
        timestamp: Timestamp.now()
      })

      setSnackbar({
        open: true,
        message: `تم ${newStatus === 'active' ? 'تفعيل' : 'تعطيل'} المستخدم بنجاح`,
        severity: 'success'
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'حدث خطأ في تغيير حالة المستخدم',
        severity: 'error'
      })
    }
  }

  // العمليات الجماعية
  const bulkToggleStatus = async (userIds: string[], newStatus: UserStatus, reason?: string) => {
    try {
      const promises = userIds.map(async (userId) => {
        const updateData: any = {
          status: newStatus,
          isActive: newStatus === 'active',
          updatedAt: Timestamp.now()
        }

        if (newStatus === 'inactive') {
          updateData.deactivatedBy = 'admin'
          updateData.deactivatedAt = Timestamp.now()
          updateData.deactivatedReason = reason || 'تم التعطيل بواسطة الإدارة'
        }

        await updateDoc(doc(db, 'users', userId), updateData)
      })

      await Promise.all(promises)
      setSelectedUsers([])
      setSnackbar({
        open: true,
        message: `تم ${newStatus === 'active' ? 'تفعيل' : 'تعطيل'} ${userIds.length} مستخدم بنجاح`,
        severity: 'success'
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'حدث خطأ في العملية الجماعية',
        severity: 'error'
      })
    }
  }

  // حذف المستخدمين المحددين
  const bulkDeleteUsers = async () => {
    try {
      let successCount = 0
      let failCount = 0
      
      const promises = selectedUsers.map(async (userId) => {
        const user = users.find(u => u.id === userId)
        if (!user) {
          failCount++
          return
        }

        try {
          // 1. حذف المستخدم من Firebase Authentication (برقم الهاتف إن وجد + UID دائماً)
          let authDeleted = false
          const phoneNumber = user.phone || ''

          // محاولة حذف جميع الحسابات المرتبطة برقم الهاتف (إن وجد)
          if (phoneNumber) {
            try {
              const deleteUsersByPhone = httpsCallable(functions, 'deleteUsersByPhone')
              console.log(`🔄 [Bulk] محاولة حذف جميع المستخدمين برقم الهاتف ${phoneNumber} من Firebase Authentication...`)
              
              const result = await deleteUsersByPhone({ phoneNumber })
              console.log(`📥 [Bulk] نتيجة Cloud Function للمستخدم ${userId} (${phoneNumber}):`, result.data)
              
              const resultData = result.data as any
              if (resultData?.success) {
                const deletedCount = resultData.deletedCount || 0
                if (deletedCount > 0) {
                  authDeleted = true
                  console.log(`✅ [Bulk] تم حذف ${deletedCount} مستخدم من Firebase Authentication للمستخدم ${userId}`)
                  console.log(`📋 [Bulk] المستخدمين المحذوفين:`, resultData.deletedUsers)
                } else {
                  console.warn(`⚠️ [Bulk] لم يتم العثور على مستخدمين برقم الهاتف ${phoneNumber} في Firebase Authentication`)
                }
              } else {
                console.warn(`⚠️ [Bulk] Cloud Function لم يؤكد الحذف للمستخدم ${userId}:`, result.data)
              }
            } catch (authError: any) {
              console.error(`❌ [Bulk] خطأ في حذف المستخدم ${userId} من Authentication باستخدام الهاتف:`, authError)
            }
          }

          // محاولة حذف المستخدم المحدد بالـ UID دائماً (حتى لو لم يكن لديه رقم هاتف)
          try {
            const deleteUserFromAuth = httpsCallable(functions, 'deleteUserFromAuth')
            console.log(`🔄 [Bulk] محاولة حذف المستخدم ${userId} مباشرة باستخدام UID...`)
            const result = await deleteUserFromAuth({ userId })
            const resultData = result.data as any
            if (resultData?.success) {
              authDeleted = true
              console.log(`✅ [Bulk] تم حذف المستخدم ${userId} من Firebase Authentication (UID)`)
            }
          } catch (directDeleteError: any) {
            console.error(`❌ [Bulk] فشل في حذف المستخدم ${userId} مباشرة باستخدام UID:`, directDeleteError)
            
            // تسجيل طلب حذف بديل ليعالجه Cloud Function processAuthDeletion
            try {
              console.log(`🔄 [Bulk] محاولة تسجيل طلب حذف بديل للمستخدم ${userId}...`)
              await addDoc(collection(db, 'pending_auth_deletions'), {
                userId,
                userName: user.name || 'غير محدد',
                userPhone: phoneNumber || null,
                deletedAt: Timestamp.now(),
                deletedBy: 'admin_dashboard_bulk',
                status: 'pending',
                error: directDeleteError.message || 'خطأ غير معروف',
              })
              console.log(`✅ [Bulk] تم تسجيل طلب حذف بديل للمستخدم ${userId} - سيتم معالجته تلقائياً`)
            } catch (fallbackError: any) {
              console.error(`❌ [Bulk] فشل في تسجيل طلب الحذف البديل للمستخدم ${userId}:`, fallbackError)
            }
          }
          
          // 2. حذف المستخدم من Firestore
          await deleteDoc(doc(db, 'users', userId))
          console.log(`✅ تم حذف المستخدم ${userId} من Firestore`)
          
          // 3. تسجيل النشاط
          try {
            await addDoc(collection(db, 'admin_activity_logs'), {
              adminId: 'current_admin',
              action: 'حذف مستخدم (جماعي)',
              targetUserId: userId,
              targetUserName: user.name || 'غير محدد',
              details: authDeleted 
                ? 'تم حذف المستخدم نهائياً من Firestore و Firebase Authentication'
                : 'تم حذف المستخدم من Firestore، طلب حذف من Authentication قيد المعالجة',
              timestamp: Timestamp.now()
            })
          } catch (logError) {
            console.warn('⚠️ فشل في تسجيل النشاط:', logError)
          }
          
          successCount++
        } catch (error: any) {
          console.error(`❌ خطأ في حذف المستخدم ${userId}:`, error)
          failCount++
        }
      })

      await Promise.all(promises)
      
      setBulkDeleteDialog(false)
      setSelectedUsers([])
      
      if (failCount === 0) {
        setSnackbar({
          open: true,
          message: `تم حذف ${successCount} مستخدم بنجاح من Firestore و Firebase Authentication`,
          severity: 'success'
        })
      } else {
        setSnackbar({
          open: true,
          message: `تم حذف ${successCount} مستخدم بنجاح، فشل حذف ${failCount} مستخدم`,
          severity: 'warning'
        })
      }
    } catch (error: any) {
      console.error('خطأ في الحذف الجماعي:', error)
      setSnackbar({
        open: true,
        message: `حدث خطأ في حذف المستخدمين: ${error.message || 'خطأ غير معروف'}`,
        severity: 'error'
      })
    }
  }

  // إرسال رسالة للمستخدمين المحددين — مع حفظ سجل كامل (من أرسل، لمن، ماذا، ونتيجة الإرسال)
  const sendMessageToUsers = async () => {
    const targetIds = [...selectedUsers]
    if (targetIds.length === 0) return

    const title = messageForm.title.trim()
    const body = messageForm.body.trim()
    if (!title || !body) {
      setSnackbar({ open: true, message: 'يرجى إدخال عنوان الرسالة ومحتواها', severity: 'error' })
      return
    }

    setSendingMessage(true)
    try {
      const adminUser = auth.currentUser
      const adminId = adminUser?.uid || 'unknown_admin'
      const adminEmail = adminUser?.email || 'غير معروف'

      // لقطة المستلمين قبل الإرسال — تبقى كما هي حتى لو تعذّر إرسال أحدهم
      const drafts: RecipientDraft[] = targetIds.map((userId) => {
        const user = users.find(u => u.id === userId)
        return {
          userId,
          name: user?.name || 'غير محدد',
          phone: user?.phone || '—',
          userType: user?.type || 'unknown',
        }
      })

      // سجل واحد لكل عملية إرسال — يُنشأ قبل الإرسال ليُربط بكل إشعار
      const { ref: logRef, recipients, error: logError } = await createAdminMessageLog({
        title,
        body,
        messageType: messageForm.type,
        adminId,
        adminEmail,
        source: 'users_page',
        sourceUrl: '/users',
        recipients: drafts,
        filters: currentFilterSnapshot(targetIds.length),
      })
      const logId = logRef?.id ?? null
      if (logError) {
        console.error('تعذّر حفظ سجل الرسالة:', logError)
      }

      const promises = targetIds.map(async (userId) => {
        const user = users.find(u => u.id === userId)
        const index = targetIds.indexOf(userId)
        if (!user) {
          recipients[index] = { ...drafts[index], status: 'failed', error: 'المستخدم غير موجود' }
          return
        }

        try {
          // إضافة الرسالة إلى مجموعة الإشعارات
          await addDoc(collection(db, 'notifications'), {
            userId,
            title,
            body,
            type: 'admin_message',
            data: {
              messageType: messageForm.type,
              sentBy: 'admin_dashboard',
              sentByAdminId: adminId,
              messageLogId: logId,
              timestamp: Timestamp.now()
            },
            isRead: false,
            read: false,
            createdAt: Timestamp.now()
          })

          // تسجيل النشاط
          await addDoc(collection(db, 'admin_activity_logs'), {
            adminId,
            adminEmail,
            action: 'إرسال رسالة',
            targetUserId: userId,
            targetUserName: user.name || 'غير محدد',
            messageLogId: logId,
            details: `تم إرسال رسالة: ${title}`,
            timestamp: Timestamp.now()
          })

          recipients[index] = { ...drafts[index], status: 'sent' }
        } catch (error: any) {
          console.error(`خطأ في إرسال الرسالة للمستخدم ${userId}:`, error)
          recipients[index] = {
            ...drafts[index],
            status: 'failed',
            error: error?.message || 'خطأ غير معروف',
          }
        }
      })

      await Promise.all(promises)

      // تثبيت النتيجة النهائية في السجل
      await finalizeAdminMessageLog(logRef, recipients)

      const successCount = recipients.filter(r => r.status === 'sent').length
      const failedCount = recipients.length - successCount

      setSendMessageDialog(false)
      setMessageForm({ title: '', body: '', type: 'notification' })
      setSelectedUsers([])

      if (logError) {
        setSnackbar({
          open: true,
          message: `أُرسلت الرسالة إلى ${successCount} مستخدم، لكن تعذّر حفظها في سجل الرسائل: ${logError}`,
          severity: 'warning'
        })
      } else if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `تم إرسال الرسالة إلى ${successCount} مستخدم بنجاح — تم حفظها في سجل الرسائل`,
          severity: 'success'
        })
      } else {
        setSnackbar({
          open: true,
          message: `تم إرسال الرسالة إلى ${successCount} مستخدم، فشل ${failedCount} — التفاصيل في سجل الرسائل`,
          severity: 'warning'
        })
      }
    } catch (error) {
      console.error('خطأ في إرسال الرسائل:', error)
      setSnackbar({
        open: true,
        message: 'حدث خطأ في إرسال الرسائل',
        severity: 'error'
      })
    } finally {
      setSendingMessage(false)
    }
  }

  // حذف مستخدم
  const deleteUser = async (user: UserDoc) => {
    try {
      let authDeleted = false
      const userId = user.id // هذا يجب أن يكون Firebase Auth UID
      const phoneNumber = user.phone || '' // رقم الهاتف
      
      console.log(`🔄 بدء عملية حذف المستخدم: ${userId}`)
      console.log(`📋 معلومات المستخدم:`, { name: user.name, phone: phoneNumber, id: userId })
      
      // 1. محاولة حذف جميع الحسابات المرتبطة برقم الهاتف (إن وجد)
      if (phoneNumber) {
        try {
          const deleteUsersByPhone = httpsCallable(functions, 'deleteUsersByPhone')
          
          console.log(`🔄 محاولة حذف جميع المستخدمين برقم الهاتف ${phoneNumber} من Firebase Authentication...`)
          
          const result = await deleteUsersByPhone({ phoneNumber })
          
          console.log(`📥 نتيجة Cloud Function (deleteUsersByPhone):`, result.data)
          
          const resultData = result.data as any
          if (resultData?.success) {
            const deletedCount = resultData.deletedCount || 0
            if (deletedCount > 0) {
              authDeleted = true
              console.log(`✅ تم حذف ${deletedCount} مستخدم من Firebase Authentication بنجاح (برقم الهاتف)`)
              console.log(`📋 المستخدمين المحذوفين:`, resultData.deletedUsers)
            } else {
              console.warn(`⚠️ لم يتم العثور على مستخدمين برقم الهاتف ${phoneNumber} في Firebase Authentication`)
            }
          } else {
            console.warn(`⚠️ Cloud Function لم يؤكد الحذف:`, result.data)
          }
          
        } catch (authError: any) {
          console.error('❌ خطأ في حذف المستخدم من Authentication باستخدام الهاتف:', authError)
          console.error('📋 تفاصيل الخطأ:', {
            code: authError.code,
            message: authError.message,
            details: authError.details
          })
        }
      } else {
        console.warn('⚠️ لا يوجد رقم هاتف لهذا المستخدم؛ سيتم حذفه بالاعتماد على UID فقط.')
      }
      
      // 2. محاولة حذف المستخدم المحدد مباشرة باستخدام UID (دائماً)
      try {
        const deleteUserFromAuth = httpsCallable(functions, 'deleteUserFromAuth')
        console.log(`🔄 محاولة حذف المستخدم ${userId} مباشرة باستخدام UID...`)
        const result = await deleteUserFromAuth({ userId })
        const resultData = result.data as any
        if (resultData?.success) {
          authDeleted = true
          console.log(`✅ تم حذف المستخدم ${userId} من Firebase Authentication (UID)`)
        }
      } catch (directDeleteError: any) {
        console.error('❌ فشل في حذف المستخدم مباشرة باستخدام UID:', directDeleteError)
        
        // في حالة فشل Cloud Function، نستخدم الطريقة البديلة (pending_auth_deletions)
        try {
          console.log(`🔄 محاولة تسجيل طلب حذف بديل للمستخدم ${userId}...`)
          await addDoc(collection(db, 'pending_auth_deletions'), {
            userId,
            userName: user.name || 'غير محدد',
            userPhone: phoneNumber || null,
            deletedAt: Timestamp.now(),
            deletedBy: 'admin_dashboard',
            status: 'pending',
            error: directDeleteError.message || 'خطأ غير معروف'
          })
          console.log('✅ تم تسجيل طلب حذف بديل - سيتم معالجته تلقائياً بواسطة processAuthDeletion')
        } catch (fallbackError: any) {
          console.error('❌ فشل في تسجيل طلب الحذف البديل:', fallbackError)
          // حتى لو فشل، نتابع حذف Firestore
        }
      }
      
      // 2. حذف المستخدم من Firestore
      try {
        await deleteDoc(doc(db, 'users', userId))
        console.log(`✅ تم حذف المستخدم ${userId} من Firestore`)
      } catch (firestoreError: any) {
        console.error('❌ خطأ في حذف المستخدم من Firestore:', firestoreError)
        throw new Error(`فشل في حذف المستخدم من Firestore: ${firestoreError.message}`)
      }
      
      // 3. تسجيل النشاط
      try {
        await addDoc(collection(db, 'admin_activity_logs'), {
          adminId: 'current_admin',
          action: 'حذف مستخدم',
          targetUserId: userId,
          targetUserName: user.name || 'غير محدد',
          details: authDeleted 
            ? `تم حذف المستخدم نهائياً من Firestore و Firebase Authentication (رقم الهاتف: ${phoneNumber})`
            : `تم حذف المستخدم من Firestore، طلب حذف من Authentication قيد المعالجة (رقم الهاتف: ${phoneNumber})`,
          timestamp: Timestamp.now()
        })
      } catch (logError) {
        console.warn('⚠️ فشل في تسجيل النشاط:', logError)
      }

      setDeleteDialog(null)
      
      if (authDeleted) {
        setSnackbar({
          open: true,
          message: `✅ تم حذف المستخدم بنجاح من Firestore و Firebase Authentication (جميع الحسابات المرتبطة برقم الهاتف)`,
          severity: 'success'
        })
      } else {
        setSnackbar({
          open: true,
          message: `⚠️ تم حذف المستخدم من Firestore. سيتم حذفه من Firebase Authentication تلقائياً قريباً`,
          severity: 'warning'
        })
      }
    } catch (error: any) {
      console.error('❌ خطأ في حذف المستخدم:', error)
      setSnackbar({
        open: true,
        message: `❌ حدث خطأ في حذف المستخدم: ${error.message || 'خطأ غير معروف'}`,
        severity: 'error'
      })
    }
  }

  // إنشاء مستخدم جديد - محدث ليطابق هيكل التطبيق
  const createUser = async (userData: Partial<UserDoc> & { password?: string }) => {
    try {
      if (!userData.phone || !userData.name || !userData.password) {
        throw new Error('جميع الحقول مطلوبة')
      }

      const phoneNumber = userData.phone as string
      const name = userData.name as string
      const password = userData.password as string
      const userType = userData.type || 'buyer'
      
      // توليد emailProxy من رقم الهاتف (نفس منطق التطبيق)
      const emailProxy = `${phoneNumber.replace(/[^0-9+]/g, '')}@phone.myapp`
      
      console.log('🔄 بدء إنشاء مستخدم جديد بواسطة الأدمن...')
      console.log('📱 رقم الهاتف:', phoneNumber)
      console.log('👤 الاسم:', name)
      console.log('🔑 نوع المستخدم:', userType)
      console.log('📧 Email Proxy:', emailProxy)

      // إنشاء حساب Firebase Auth
      // استخدام Auth ثانوي لإنشاء المستخدمين بدون التأثير على جلسة الأدمن الحالية
      const auth = getAdminUserCreateAuth()
      
      let firebaseUser
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, emailProxy, password)
        firebaseUser = userCredential.user
        console.log('✅ تم إنشاء حساب Firebase Auth:', firebaseUser.uid)
        
        // تحديث اسم المستخدم في Firebase Auth (مع رقم الهاتف للتوافق)
        try {
          await updateProfile(firebaseUser, { 
            displayName: `${name} (${phoneNumber})`,
            photoURL: `tel:${phoneNumber}` // استخدام photoURL لحفظ رقم الهاتف
          })
          console.log('✅ تم تحديث اسم المستخدم في Firebase Auth مع رقم الهاتف')
        } catch (updateError) {
          console.warn('⚠️ تحذير: فشل في تحديث اسم المستخدم:', updateError)
        }
        
      } catch (authError: any) {
        console.error('❌ خطأ في إنشاء حساب Firebase Auth:', authError)
        if (authError.code === 'auth/email-already-in-use') {
          throw new Error('رقم الهاتف مستخدم بالفعل')
        }
        throw new Error(`فشل في إنشاء الحساب: ${authError.message}`)
      }

      const firebaseUid = firebaseUser.uid
      const isSeller = userType === 'seller'
      // جميع الحسابات الجديدة (مشتري وتاجر) تكون مفعلة افتراضياً
      const defaultStatus = 'active'

      // إنشاء بيانات المستخدم بنفس هيكل التطبيق
      const userDataComplete: any = {
        // المعرفات
        id: firebaseUid,                    // Firebase UID كـ Document ID
        firebaseUid: firebaseUid,           // Firebase UID كحقل منفصل للتوافق
        name: name,
        phone: phoneNumber,
        password: password,                 // حفظ كلمة المرور للتوافق مع النظام القديم
        emailProxy: emailProxy,            // Email Proxy
        
        // نوع المستخدم
        type: userType,
        
        // التقييمات (نفس قيم التطبيق)
        rating: 5.0,                      // التقييم الافتراضي 5.0
        averageRating: 5.0,               // متوسط التقييم
        totalRatings: 0,                  // عدد التقييمات
        
        // حالة المستخدم
        status: defaultStatus,
        isActive: true,                   // للتوافق مع النظام القديم
        
        // التحقق
        isVerified: true,                 // المستخدمون المضافون بواسطة الأدمن محققون
        verified: true,                   // للتوافق مع الكود القديم
        
        // حقول التعطيل (جميع الحسابات الجديدة مفعلة - لا تعطيل)
        deactivatedBy: null,
        deactivatedAt: null,
        deactivatedReason: null,
        
        // التواريخ
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        
        // حقول إضافية
        fcmToken: null,                   // سيتم تحديثه عند تسجيل الدخول من التطبيق
        address: userData.address || '',
        categories: userData.categories || [],
        businessLicense: (userData as any).businessLicense || '',
        profileImage: (userData as any).profileImage || '',
        latitude: (userData as any).latitude || null,
        longitude: (userData as any).longitude || null,
      }

      // حفظ في Firestore باستخدام Firebase UID كـ Document ID
      await setDoc(doc(db, 'users', firebaseUid), userDataComplete, { merge: true } as any)
      console.log('✅ تم حفظ البيانات في Firestore مع Firebase UID:', firebaseUid)

      // ملاحظة: رقم الهاتف محفوظ في displayName و photoURL للتوافق مع Firebase Auth Console

      // تسجيل خروج من الـ Auth الثانوي فقط (لا يؤثر على جلسة الأدمن الرئيسية)
      await auth.signOut()

      setCreateDialog(false)
      setCreateForm({ name: '', phone: '', type: 'buyer', password: '', confirm: '' })
      setSnackbar({
        open: true,
        message: 'تم إنشاء المستخدم بنجاح مع حساب Firebase Auth كامل',
        severity: 'success'
      })
      
      console.log('🎉 تم إكمال إنشاء المستخدم بنجاح!')
    } catch (error: any) {
      console.error('❌ خطأ في إنشاء المستخدم:', error)
      setSnackbar({
        open: true,
        message: `حدث خطأ في إنشاء المستخدم: ${error.message}`,
        severity: 'error'
      })
    }
  }

  const saveEdit = async () => {
    if (!edit) return
    try {
      await updateDoc(doc(db, 'users', edit.id), {
        name: edit.name ?? '',
        phone: edit.phone ?? '',
        address: edit.address ?? '',
        categories: normalizeCategoryIdList(edit.categories ?? [], allCategories),
        allowedCategories: normalizeCategoryIdList(edit.allowedCategories ?? [], allCategories),
        updatedAt: Timestamp.now()
      })
      setEdit(null)
      setSnackbar({
        open: true,
        message: 'تم تحديث بيانات المستخدم بنجاح',
        severity: 'success'
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'حدث خطأ في تحديث البيانات',
        severity: 'error'
      })
    }
  }

  // حساب البيانات للصفحة الحالية
  const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(filteredUsers.length / pageSize)

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        نظام إدارة المستخدمين المتقدم
      </Typography>

      {/* الإحصائيات */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.totalUsers}</Typography>
              <Typography variant="body2">إجمالي المستخدمين</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.activeBuyers}</Typography>
              <Typography variant="body2">مشترون نشطون</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">{stats.inactiveBuyers}</Typography>
              <Typography variant="body2">مشترون غير نشطين</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.activeSellers}</Typography>
              <Typography variant="body2">تجار نشطون</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">{stats.inactiveSellers}</Typography>
              <Typography variant="body2">تجار غير نشطين</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="secondary.main">{stats.admins}</Typography>
              <Typography variant="body2">مديرون</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* أدوات البحث والفلترة */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="البحث بالاسم أو رقم الهاتف..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>نوع المستخدم</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as UserType | 'all')}
                  label="نوع المستخدم"
                >
                  <MenuItem value="all">جميع الأنواع</MenuItem>
                  <MenuItem value="buyer">مشتري</MenuItem>
                  <MenuItem value="seller">تاجر</MenuItem>
                  <MenuItem value="admin">مدير</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as UserStatus | 'all')}
                  label="الحالة"
                >
                  <MenuItem value="all">جميع الحالات</MenuItem>
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialog(true)}
                sx={{ height: 56 }}
              >
                إضافة مستخدم
              </Button>
            </Grid>
          </Grid>

          {/* فلتر الفئات — يظهر للتجار فقط */}
          {typeFilter === 'seller' && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <FilterIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  فلتر الفئات
                </Typography>
                <Chip
                  size="small"
                  color={sellerCategoryFilterActive ? 'primary' : 'default'}
                  label={
                    sellerCategoryFilterActive
                      ? `${sellerCategoryKeys.size} فئة محددة`
                      : 'كل الفئات'
                  }
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${sellersMatchingCategoryFilter} من ${totalSellersCount} تاجر`}
                />
                <Button size="small" onClick={() => setSellerCategoryFilterOpen((o) => !o)}>
                  {sellerCategoryFilterOpen ? 'إخفاء الفلتر' : 'إظهار الفلتر'}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" onClick={selectAllSellerCategories} disabled={sellerCategoryOptions.length === 0}>
                  تحديد الكل
                </Button>
                <Button size="small" onClick={clearAllSellerCategories} disabled={sellerCategoryKeys.size === 0}>
                  إلغاء الكل
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                حدّد فئة واحدة أو أكثر لعرض التجار المسجّلين فيها فقط. الرقم بجانب كل فئة = عدد التجار.
                التحديد يعمل بمنطق «أي من الفئات المحددة» (OR).
              </Typography>

              <Collapse in={sellerCategoryFilterOpen || sellerCategoryKeys.size > 0}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  <TextField
                    size="small"
                    placeholder="ابحث عن فئة..."
                    value={sellerCategorySearch}
                    onChange={(e) => setSellerCategorySearch(e.target.value)}
                    sx={{ minWidth: 220, flex: '0 1 320px' }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={includeAllowedCategories}
                        onChange={(e) => setIncludeAllowedCategories(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        تضمين الفئات المسموح بها فقط من الأدمن
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Stack>

                <Box
                  sx={{
                    mt: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
                    gap: 0.25,
                    maxHeight: 260,
                    overflow: 'auto',
                    pr: 0.5,
                  }}
                >
                  {visibleSellerCategoryOptions.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                      {sellerCategoryOptions.length === 0
                        ? 'لا توجد فئات مسجّلة في الإعدادات بعد.'
                        : 'لا توجد فئات مطابقة للبحث.'}
                    </Typography>
                  )}
                  {visibleSellerCategoryOptions.map((opt) => {
                    const selected = sellerCategoryKeys.has(opt.key)
                    return (
                      <Tooltip
                        key={opt.key}
                        title={opt.isLegacy ? 'اسم فئة قديم لم يعد موجوداً في الإعدادات' : opt.label}
                        placement="top"
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selected}
                              onChange={() => toggleSellerCategory(opt.key)}
                              disabled={opt.count === 0 && !selected}
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ opacity: opt.count === 0 ? 0.55 : 1 }}>
                                {opt.label}
                              </Typography>
                              {opt.isLegacy && (
                                <Typography variant="caption" color="warning.main" noWrap>
                                  (قديم)
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" noWrap>
                                ({opt.count})
                              </Typography>
                            </Box>
                          }
                          sx={{ m: 0 }}
                        />
                      </Tooltip>
                    )
                  })}
                  {sellersWithoutCategory > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={sellerCategoryKeys.has(NO_CATEGORY_KEY)}
                          onChange={() => toggleSellerCategory(NO_CATEGORY_KEY)}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            بدون فئة محددة
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({sellersWithoutCategory})
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0 }}
                    />
                  )}
                </Box>
              </Collapse>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* العمليات الجماعية */}
      {selectedUsers.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'action.hover' }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography>تم تحديد {selectedUsers.length} مستخدم</Typography>
              <Button
                variant="contained"
                color="success"
                onClick={() => bulkToggleStatus(selectedUsers, 'active')}
              >
                تفعيل المحددين
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => bulkToggleStatus(selectedUsers, 'inactive')}
              >
                تعطيل المحددين
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={<AddIcon />}
                onClick={() => setSendMessageDialog(true)}
              >
                إرسال رسالة
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setBulkDeleteDialog(true)}
              >
                حذف المحددين
              </Button>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => navigate('/message-history')}
              >
                سجل الرسائل
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedUsers([])}
              >
                إلغاء التحديد
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* جدول المستخدمين */}
      <TableContainer
        component={Paper}
        sx={{
          mb: 3,
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          borderRadius: { xs: 2, md: 3 },
          boxShadow: { xs: 0, md: 1 },
          border: { xs: '1px solid', md: 'none' },
          borderColor: { xs: 'divider', md: 'transparent' },
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: { xs: 900, md: '100%' },
            '& th, & td': {
              whiteSpace: { xs: 'nowrap', md: 'normal' },
              px: { xs: 1, md: 2 },
              py: { xs: 1, md: 1.5 }
            },
            '& th': {
              bgcolor: 'grey.50'
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={paginatedUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers(filteredUsers.map(u => u.id))
                    } else {
                      setSelectedUsers([])
                    }
                  }}
                />
              </TableCell>
              <TableCell><strong>الاسم</strong></TableCell>
              <TableCell><strong>رقم الهاتف</strong></TableCell>
              <TableCell><strong>النوع</strong></TableCell>
              <TableCell><strong>الحالة</strong></TableCell>
              <TableCell><strong>التقييم</strong></TableCell>
              <TableCell><strong>تاريخ التسجيل</strong></TableCell>
              <TableCell><strong>الإجراءات</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => {
                      if (selectedUsers.includes(user.id)) {
                        setSelectedUsers(selectedUsers.filter(id => id !== user.id))
                      } else {
                        setSelectedUsers([...selectedUsers, user.id])
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Stack>
                    <Typography variant="body2" fontWeight="medium">
                      {user.name || 'غير محدد'}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {user.isVerified && (
                        <Chip label="محقق" size="small" color="success" sx={{ width: 'fit-content' }} />
                      )}
                      {user.isDeleted === true && user.deletedBy === 'self' && (
                        <Chip label="محذوف من المستخدم" size="small" color="warning" sx={{ width: 'fit-content' }} />
                      )}
                      {user.isDeleted === true && user.deletedBy !== 'self' && (
                        <Chip label="محذوف" size="small" color="error" sx={{ width: 'fit-content' }} />
                      )}
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>{user.phone || '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip 
                      label={user.type === 'buyer' ? 'مشتري' : user.type === 'seller' ? 'تاجر' : user.type === 'admin' ? 'مدير' : 'غير محدد'} 
                      color={user.type === 'buyer' ? 'primary' : user.type === 'seller' ? 'secondary' : user.type === 'admin' ? 'warning' : 'default'}
                      size="small"
                    />
                    {user.type === 'seller' && (
                      <Tooltip title={user.isSuperSeller ? 'تاجر سوبر - إلغاء التفعيل' : 'تفعيل تاجر سوبر'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleSuperSeller(user)}
                          sx={{
                            color: user.isSuperSeller ? 'warning.main' : 'action.disabled',
                            '&:hover': {
                              color: 'warning.main',
                              backgroundColor: 'warning.light',
                            }
                          }}
                        >
                          {user.isSuperSeller ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={1}>
                    <Chip 
                      label={
                        user.isDeleted === true
                          ? (user.deletedBy === 'self' ? 'محذوف (مستخدم)' : 'محذوف')
                          : ((user.status === 'active' || user.isActive) ? 'نشط' : 'غير نشط')
                      }
                      color={
                        user.isDeleted === true
                          ? 'warning'
                          : ((user.status === 'active' || user.isActive) ? 'success' : 'error')
                      }
                      size="small"
                    />
                    {user.deactivatedBy === 'admin' && user.deactivatedReason && (
                      <Typography variant="caption" color="text.secondary">
                        {user.deactivatedReason}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {user.rating ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2">{user.rating.toFixed(1)}</Typography>
                      <Typography variant="body2" color="warning.main">⭐</Typography>
                    </Stack>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  {user.createdAt ? (
                    user.createdAt.toDate ? user.createdAt.toDate().toLocaleDateString('ar-EG') : new Date(user.createdAt).toLocaleDateString('ar-EG')
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="تعديل">
                      <IconButton size="small" onClick={() => {
                        const normalized: UserDoc = {
                          ...user,
                          categories: normalizeCategoryIdList(user.categories ?? [], allCategories),
                          allowedCategories: normalizeCategoryIdList(user.allowedCategories ?? [], allCategories),
                        }
                        setEdit(normalized)
                      }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={(user.status === 'active' || user.isActive) ? 'تعطيل' : 'تفعيل'}>
                      <IconButton 
                        size="small" 
                        color={(user.status === 'active' || user.isActive) ? 'error' : 'success'}
                        onClick={() => toggleUserStatus(user)}
                      >
                        {(user.status === 'active' || user.isActive) ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="حذف">
                      <IconButton size="small" color="error" onClick={() => setDeleteDialog(user)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="large"
          />
        </Stack>
      )}

      {/* حوار التعديل */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
        <DialogTitle>تعديل بيانات المستخدم</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField 
              label="الاسم" 
              value={edit?.name || ''} 
              onChange={e => setEdit({ ...(edit as UserDoc), name: e.target.value })} 
            />
            <TextField 
              label="رقم الهاتف" 
              value={edit?.phone || ''} 
              onChange={e => setEdit({ ...(edit as UserDoc), phone: e.target.value })} 
            />
            <TextField
              label="البريد الإلكتروني المسجل"
              value={edit?.email || edit?.emailProxy || ''}
              InputProps={{ readOnly: true }}
              helperText={(!edit?.email && edit?.emailProxy) ? 'هذا بريد داخلي (Proxy) مبني على رقم الهاتف' : ''}
            />
            <TextField 
              label="العنوان" 
              value={edit?.address || ''} 
              onChange={e => setEdit({ ...(edit as UserDoc), address: e.target.value })} 
            />

            {/* إحصائيات المشتري */}
            {edit?.type === 'buyer' && edit?.buyerStats && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  إحصائيات المشتري
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">نقاط السمعة:</Typography>
                    <Typography variant="h6" color={edit.buyerStats.buyerScore && edit.buyerStats.buyerScore >= 60 ? 'success.main' : edit.buyerStats.buyerScore && edit.buyerStats.buyerScore >= 40 ? 'warning.main' : 'error.main'}>
                      {edit.buyerStats.buyerScore?.toFixed(1) || '0.0'} / 100
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">إجمالي الطلبات:</Typography>
                    <Typography variant="h6">{edit.buyerStats.totalRequests || 0}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">مكتملة:</Typography>
                    <Typography variant="body1" color="success.main">{edit.buyerStats.completedRequests || 0}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">ملغاة:</Typography>
                    <Typography variant="body1" color="error.main">{edit.buyerStats.cancelledRequests || 0}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">منتهية:</Typography>
                    <Typography variant="body1" color="warning.main">{edit.buyerStats.expiredRequests || 0}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">نسبة القبول:</Typography>
                    <Typography variant="body1">{edit.buyerStats.acceptanceRate?.toFixed(1) || '0.0'}%</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">نسبة الإلغاء:</Typography>
                    <Typography variant="body1" color={edit.buyerStats.cancellationRate && edit.buyerStats.cancellationRate > 50 ? 'error.main' : 'text.primary'}>
                      {edit.buyerStats.cancellationRate?.toFixed(1) || '0.0'}%
                    </Typography>
                  </Grid>
                  {edit.buyerStats.isRestricted && (
                    <Grid item xs={12}>
                      <Chip
                        label={`مقيد (مستوى ${edit.buyerStats.restrictionLevel || 0})`}
                        color="error"
                        size="small"
                      />
                      {edit.buyerStats.restrictionEndDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          حتى: {edit.buyerStats.restrictionEndDate.toDate ? new Date(edit.buyerStats.restrictionEndDate.toDate()).toLocaleString('ar-SA') : 'غير محدد'}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* جدول التحكم بالفئات */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>الفئة</strong></TableCell>
                    <TableCell align="center"><strong>مسموح للتاجر (من الأدمن)</strong></TableCell>
                    <TableCell align="center"><strong>نشط عند التاجر</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allCategories.map(cat => {
                    const allowed = listHasCategory(edit?.allowedCategories, cat)
                    const active = listHasCategory(edit?.categories, cat)
                    return (
                      <TableRow key={cat.id} hover>
                        <TableCell>{cat.name}</TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={allowed}
                            onChange={async (e) => {
                              if (!edit) return
                              const allowedArr = e.target.checked
                                ? addCategoryIdToList(edit.allowedCategories ?? [], cat)
                                : removeCategoryFromList(edit.allowedCategories ?? [], cat)
                              let categoriesArr = edit.categories ?? []
                              if (!e.target.checked) {
                                categoriesArr = removeCategoryFromList(categoriesArr, cat)
                              }
                              const normalizedAllowed = normalizeCategoryIdList(allowedArr, allCategories)
                              const normalizedCategories = normalizeCategoryIdList(categoriesArr, allCategories)
                                .filter((id) => normalizedAllowed.includes(id))
                              await updateDoc(doc(db, 'users', edit.id), {
                                allowedCategories: normalizedAllowed,
                                categories: normalizedCategories,
                                updatedAt: Timestamp.now()
                              })
                              setEdit({
                                ...(edit as UserDoc),
                                allowedCategories: normalizedAllowed,
                                categories: normalizedCategories,
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={active}
                            disabled={!allowed}
                            onChange={async (e) => {
                              if (!edit) return
                              const catsArr = e.target.checked
                                ? addCategoryIdToList(edit.categories ?? [], cat)
                                : removeCategoryFromList(edit.categories ?? [], cat)
                              const normalizedCategories = normalizeCategoryIdList(catsArr, allCategories)
                              await updateDoc(doc(db, 'users', edit.id), {
                                categories: normalizedCategories,
                                updatedAt: Timestamp.now()
                              })
                              setEdit({ ...(edit as UserDoc), categories: normalizedCategories })
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>إلغاء</Button>
          <Button variant="contained" onClick={saveEdit}>حفظ</Button>
        </DialogActions>
      </Dialog>

      {/* حوار إنشاء مستخدم */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>إضافة مستخدم جديد</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="الاسم"
              value={createForm.name}
              onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
              required
            />
            <TextField
              label="رقم الهاتف"
              value={createForm.phone}
              onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
              placeholder="مثال: +2010xxxxxxxx"
              required
            />
            <FormControl fullWidth>
              <InputLabel>نوع المستخدم</InputLabel>
              <Select
                value={createForm.type}
                label="نوع المستخدم"
                onChange={e => setCreateForm({ ...createForm, type: e.target.value as UserType })}
              >
                <MenuItem value="buyer">مشتري</MenuItem>
                <MenuItem value="seller">تاجر</MenuItem>
                <MenuItem value="admin">مدير</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="كلمة المرور"
              type="password"
              value={createForm.password}
              onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
              required
            />
            <TextField
              label="تأكيد كلمة المرور"
              type="password"
              value={createForm.confirm}
              onChange={e => setCreateForm({ ...createForm, confirm: e.target.value })}
              required
            />
            <Alert severity="info">
              سيُنشأ التاجر بحالة غير نشط ويحتاج موافقة الإدارة. المشترون والمديرون يُنشأون نشطين.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>إلغاء</Button>
          <Button 
            variant="contained"
            onClick={() => {
              if (!createForm.phone || !createForm.type) {
                setSnackbar({ open: true, message: 'يرجى إدخال رقم الهاتف وتحديد النوع', severity: 'error' })
                return
              }
              if (!createForm.password || createForm.password.length < 6) {
                setSnackbar({ open: true, message: 'كلمة المرور يجب ألا تقل عن 6 أحرف', severity: 'error' })
                return
              }
              if (createForm.password !== createForm.confirm) {
                setSnackbar({ open: true, message: 'كلمتا المرور غير متطابقتين', severity: 'error' })
                return
              }
              createUser({
                name: createForm.name || 'مستخدم',
                phone: createForm.phone,
                type: createForm.type,
                password: createForm.password,
              })
            }}
          >
            إنشاء
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار الحذف */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف المستخدم "{deleteDialog?.name}"؟ هذا الإجراء لا يمكن التراجع عنه.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>إلغاء</Button>
          <Button variant="contained" color="error" onClick={() => deleteDialog && deleteUser(deleteDialog)}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار الحذف الجماعي */}
      <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)}>
        <DialogTitle>تأكيد الحذف الجماعي</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف {selectedUsers.length} مستخدم؟ هذا الإجراء لا يمكن التراجع عنه.
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            سيتم حذف المستخدمين نهائياً من قاعدة البيانات وحسابات المصادقة.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>إلغاء</Button>
          <Button variant="contained" color="error" onClick={bulkDeleteUsers}>
            حذف {selectedUsers.length} مستخدم
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار إرسال الرسالة */}
      <Dialog
        open={sendMessageDialog}
        onClose={() => !sendingMessage && setSendMessageDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>إرسال رسالة للمستخدمين المحددين</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" icon={<HistoryIcon fontSize="inherit" />}>
              سيتم حفظ هذه الرسالة في «سجل الرسائل» باسمك مع قائمة كل المستلمين ونتيجة الإرسال،
              حتى يمكن مراجعتها لاحقاً.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              سيتم إرسال الرسالة إلى {selectedUsers.length} مستخدم
            </Typography>
            {selectedUsers.length > 0 && (
              <Box sx={{ maxHeight: 140, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {selectedUsers.slice(0, 60).map((id) => {
                    const u = users.find(x => x.id === id)
                    return (
                      <Chip
                        key={id}
                        size="small"
                        variant="outlined"
                        label={`${u?.name || 'غير محدد'}${u?.phone ? ` — ${u.phone}` : ''}`}
                      />
                    )
                  })}
                  {selectedUsers.length > 60 && (
                    <Chip size="small" label={`+${selectedUsers.length - 60}`} />
                  )}
                </Stack>
              </Box>
            )}
            {sellerCategoryFilterActive && (
              <Alert severity="warning">
                الإرسال يتم لتحديدك اليدوي فقط. الفلاتر الحالية (بما فيها فئات التجار) لا تُطبَّق على
                الإرسال — إن أردت إرسال الرسالة لكل التجار المطابقين استخدم مربع التحديد في أعلى الجدول.
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>نوع الرسالة</InputLabel>
              <Select
                value={messageForm.type}
                label="نوع الرسالة"
                onChange={e => setMessageForm({ ...messageForm, type: e.target.value as 'notification' | 'email' })}
              >
                <MenuItem value="notification">إشعار داخل التطبيق</MenuItem>
                <MenuItem value="email">رسالة بريد إلكتروني</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="عنوان الرسالة"
              value={messageForm.title}
              onChange={e => setMessageForm({ ...messageForm, title: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="محتوى الرسالة"
              value={messageForm.body}
              onChange={e => setMessageForm({ ...messageForm, body: e.target.value })}
              multiline
              rows={4}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendMessageDialog(false)} disabled={sendingMessage}>إلغاء</Button>
          <Button
            variant="contained"
            onClick={sendMessageToUsers}
            disabled={sendingMessage || !messageForm.title.trim() || !messageForm.body.trim() || selectedUsers.length === 0}
            startIcon={sendingMessage ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {sendingMessage ? 'جارٍ الإرسال...' : `إرسال إلى ${selectedUsers.length} مستخدم`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar للرسائل */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
